import { useEffect, useState } from 'react';

import { useTranslation } from '@crehana/i18n';

interface IUseModalDeleteProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => Promise<void> | void;
  refetch?: () => void;
  noValidation?: boolean;
}

const useModalDelete = ({
  open,
  onDelete,
  onClose,
  refetch,
  noValidation,
}: IUseModalDeleteProps) => {
  const { languageCode } = useTranslation();
  const [textConfirmation, setTextConfirmation] = useState('');
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const textToConfirm = languageCode === 'es' ? 'ELIMINAR' : 'EXCLUIR';
  const disabled = textConfirmation !== textToConfirm;

  const handleOpenModalDelete = () => setOpenModalDelete(true);

  const closeModalDelete = () => {
    setOpenModalDelete(false);
    onClose();
  };

  const handleConfirmFirstModal = () => {
    setOpenModalDelete(false);
    setOpenModalConfirm(true);
  };

  const handleCloseModalConfirm = () => {
    setOpenModalConfirm(false);
    onClose();
  };

  const handleConfirmSecondModal = async () => {
    if (onDelete.constructor.name === 'AsyncFunction') {
      await onDelete();
    } else {
      onDelete();
    }
    handleCloseModalConfirm();
    refetch?.();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!open) return;
    if (noValidation) {
      handleConfirmFirstModal();
      return;
    }
    handleOpenModalDelete();
  }, [open]);

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!openModalDelete) return;
    setTextConfirmation('');
  }, [openModalDelete]);

  return {
    openModalDelete,
    onCloseModalDelete: closeModalDelete,
    disabledModalDelete: disabled,
    openModalDeleteConfirm: openModalConfirm,
    onConfirmModalDelete: handleConfirmFirstModal,
    onConfirmModalDeleteConfirm: handleConfirmSecondModal,
    onCloseModalDeleteConfirm: handleCloseModalConfirm,
    textConfirmation,
    textToConfirm,
    setTextConfirmation,
    handleOpenModalDelete,
    closeModalDelete,
  };
};

export default useModalDelete;
